const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'
const BACKEND_URL = API_BASE.replace(/\/api\/v1$/, '')

export class ApiError extends Error {
  public status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function getFileUrl(path: string | undefined | null): string {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return `${BACKEND_URL}${path}`
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('admin_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...(options?.headers as Record<string, string>) },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    if ((res.status === 401 || res.status === 403) && !endpoint.includes('/users/me') && !endpoint.includes('/auth/login')) {
      localStorage.removeItem('admin_token')
      window.location.href = res.status === 403 ? '/login?error=forbidden' : '/login'
    }
    throw new ApiError(res.status, body.message || `API error ${res.status}`)
  }

  return res.json()
}

export const api = {
  auth: {
    login: (data: { email: string; password: string }) =>
      request<{ token: string; user: Record<string, unknown> }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  users: {
    getMe: () => request<Record<string, unknown>>('/users/me'),
  },
  admin: {
    getHomeConfig: () =>
      request<Record<string, unknown>>('/admin/home-config'),
    updateHomeConfig: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/home-config', { method: 'PUT', body: JSON.stringify(data) }),

    dashboard: () =>
      request<{
        users: number
        courses: number
        videos: number
        teachers: number
        subscriptions: number
        enrollments: number
        posts: number
        revenue: number
      }>('/admin/dashboard'),

    getCourses: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/courses?${q}`)
    },
    createCourse: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/courses', { method: 'POST', body: JSON.stringify(data) }),
    updateCourse: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/courses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteCourse: (id: string) =>
      request<{ message: string }>(`/admin/courses/${id}`, { method: 'DELETE' }),

    getLessons: (courseId: string) =>
      request<Record<string, unknown>[]>(`/admin/courses/${courseId}/lessons`),
    createLesson: (courseId: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/courses/${courseId}/lessons`, { method: 'POST', body: JSON.stringify(data) }),
    updateLesson: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/lessons/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteLesson: (id: string) =>
      request<{ message: string }>(`/admin/lessons/${id}`, { method: 'DELETE' }),

    getVideos: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/videos?${q}`)
    },
    createVideo: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/videos', { method: 'POST', body: JSON.stringify(data) }),
    updateVideo: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/videos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteVideo: (id: string) =>
      request<{ message: string }>(`/admin/videos/${id}`, { method: 'DELETE' }),

    getTeachers: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/teachers?${q}`)
    },
    createTeacher: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/teachers', { method: 'POST', body: JSON.stringify(data) }),
    updateTeacher: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/teachers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTeacher: (id: string) =>
      request<{ message: string }>(`/admin/teachers/${id}`, { method: 'DELETE' }),

    getPosts: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/posts?${q}`)
    },
    deletePost: (id: string) =>
      request<{ message: string }>(`/admin/posts/${id}`, { method: 'DELETE' }),

    getUsers: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/users?${q}`)
    },
    updateUser: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteUser: (id: string) =>
      request<{ message: string }>(`/admin/users/${id}`, { method: 'DELETE' }),

    // Payments
    getPayments: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{
        data: Record<string, unknown>[]
        pagination: { page: number; limit: number; total: number; pages: number }
        stats: { totalRevenue: number; completedCount: number; pendingCount: number; failedCount: number }
      }>(`/admin/payments?${q}`)
    },
    updatePayment: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    // Payment Settings
    getPaymentSettings: () =>
      request<Record<string, unknown>>('/admin/payment-settings'),
    updatePaymentSettings: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/payment-settings', { method: 'PUT', body: JSON.stringify(data) }),

    // Categories
    getCategories: () =>
      request<{ courseCategories: string[]; videoCategories: string[] }>('/admin/categories'),
    updateCategories: (data: { courseCategories: string[]; videoCategories: string[] }) =>
      request<Record<string, unknown>>('/admin/categories', { method: 'PUT', body: JSON.stringify(data) }),

    // My Permissions
    getMyPermissions: () =>
      request<{ isSuperAdmin: boolean; permissions: string[] }>('/admin/my-permissions'),

    // System Users
    getSystemUsers: (params?: Record<string, string>) => {
      const q = new URLSearchParams(params)
      return request<{ data: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number; pages: number } }>(`/admin/system-users?${q}`)
    },
    createSystemUser: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/admin/system-users', { method: 'POST', body: JSON.stringify(data) }),
    updateSystemUser: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/system-users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteSystemUser: (id: string) =>
      request<{ message: string }>(`/admin/system-users/${id}`, { method: 'DELETE' }),
  },
}
